import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthSelectors,
  ConfigActions,
  ConfigSelectors,
  FirebaseActions,
  HistogramActions,
  HistogramSelectors,
  ProgressActions,
  ProgressSelectors,
  StatsSelectors,
  preloadAgeGroupSaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { AxiosResponse } from 'axios';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import { all, call, delay, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { AppActions } from '../app/slice';
import history from '../base/historyService';
import { CyclesActions, CyclesSelectors, CyclesTypes } from '../cycles';
import * as Selectors from './selectors';
import SessionCache from './sessionCache';
import { SessionActions } from './slice';
import {
  ISaveWorkoutStatePayload,
  ITrainingWorkoutState,
  ITrainingWorkoutStateResponse,
  IWorkoutStats,
  SessionError,
} from './types';
import WorkoutStateService from './workoutStateService';

export function* loadWorkoutStates() {
  let workoutstates = yield call(loadCachedWorkoutStatesSaga);
  try {
    if (!workoutstates) {
      const apiResponse: AxiosResponse<ITrainingWorkoutStateResponse> = yield call(
        WorkoutStateService.getTrainingWorkoutState
      );
      workoutstates = apiResponse.data.trainingworkoutstate;
      yield call(SessionCache.setWorkoutStates, workoutstates);
    }
    yield put(SessionActions.updateWorkoutStates(workoutstates));
  } catch (error) {
    workoutstates = [];
    yield put(SessionActions.updateWorkoutStates(workoutstates));
    throw new SessionError('Workout states loading failed');
  }
}

export function* loadCachedWorkoutStatesSaga() {
  try {
    return yield call(SessionCache.getWorkoutStates);
  } catch (error: any) {
    const page = history.createHref(history.location);
    yield put(FirebaseActions.firebaseErrorReportAction(page, 'Session', error.message));
  }
}

// load or calculate goal
export function* getWorkoutGoal(workoutId: number, siblings: number[]) {
  // try to get previously calculated goal for given workout
  let goal = yield select(Selectors.getWorkoutGoal, workoutId);
  if (goal) {
    return goal;
  }
  // if goal was not calculated before - try to calculate it from progress of siblings
  // we need full progress to be loaded here
  yield call(waitForFullProgressSaga);
  goal = yield select(ProgressSelectors.getNextWorkoutGoalWithSibling, siblings);
  if (!goal) {
    // if progress is empty - try to calculate it from histogram
    goal = yield select(HistogramSelectors.getWorkoutGoal, workoutId);
    // fallback to 1 if histogram data was also not enough
    goal = goal > 0 ? goal : 1;
  }
  return goal;
}

export function* loadWorkoutStats(workoutId: number) {
  // get sibling Ids
  const siblings: number[] = yield select(ConfigSelectors.getWorkoutIdsOfCategoryGroup, workoutId);
  // load last progress for all workouts in session
  yield put(ProgressActions.loadLastProgress(siblings));
  // wait until all needed data is loaded
  let loaded = false;
  do {
    // we need last progress for given workout and its siblings
    const siblingsProgressLoaded = yield select(ProgressSelectors.isLastProgressLoadedForWorkouts, siblings);
    // and histogram for the same workout
    const histogramLoaded = yield select(HistogramSelectors.isWorkoutHistogramLoaded, workoutId);
    loaded = histogramLoaded && siblingsProgressLoaded;
    if (!loaded) {
      yield delay(300);
    }
  } while (!loaded);
  // calculate comparison percentage for given workout
  const score: number = yield select(ProgressSelectors.getWorkoutLastScoreWithSiblings, siblings);
  const comparison = yield select(HistogramSelectors.getWorkoutComparison, workoutId, score);
  // calculate goal taking into account also siblings progress
  const goal = yield call(getWorkoutGoal, workoutId, siblings);
  // get locked status for given workout
  const isPremium = yield select(ConfigSelectors.isWorkoutPremium, workoutId);
  const userIsPremium = yield select(AuthSelectors.isPremium);
  const locked = isPremium && !userIsPremium;
  // get exercise name for workout
  const isChallenge = yield select(ConfigSelectors.isChallengeWorkout, workoutId);
  const exerciseName = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId, isChallenge);
  // update the stats
  const workoutStats: IWorkoutStats = {
    comparison,
    goal,
    locked,
    exerciseName,
    siblings,
  };
  yield put(SessionActions.updateWorkoutStats(workoutId, workoutStats));
}

export function* initFeatureSaga(action: PayloadAction<number>) {
  const userId = action.payload;
  yield call([SessionCache, 'init'], userId);
  yield call(WorkoutStateService.init, ApiService);
}

export function* waitForCycles() {
  const cyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  if (cyclesLoading) {
    // wait for cycle feature
    yield take(CyclesActions.cyclesReady);
  }
}

export function* waitForConfig() {
  const configLoading = yield select(ConfigSelectors.isConfigLoading);
  if (configLoading) {
    // wait for config feature
    yield take(ConfigActions.configReady);
  }
}

export function* loadSessionDataSaga() {
  yield call(waitForCycles);
  yield call(waitForConfig);

  const domainId = yield select(CyclesSelectors.getCurrentSessionDomain);
  yield put(SessionActions.updateDomain(domainId));

  const workouts: CyclesTypes.IWorkoutClientStatus[] = yield select(
    CyclesSelectors.getCurrentSessionWorkoutsWithoutCFQ
  );
  const workoutIds = map(workouts, (w) => w.workoutId);
  yield put(SessionActions.updateSession(workoutIds));

  const disabled: boolean[] = [];
  for (const workoutId of workoutIds) {
    const isWorkoutEnabled = yield select(ConfigSelectors.isWorkoutReleasedForWeb, workoutId);
    disabled.push(!isWorkoutEnabled);
  }
  yield put(SessionActions.updateDisabled(disabled));

  const completed = map(
    workouts,
    (w) => w.status === CyclesTypes.ETrainingStatus.Finished || w.status === CyclesTypes.ETrainingStatus.Skipped
  );
  yield put(SessionActions.updateCompleted(completed));

  // strip repeated workouts
  const uniqIds = uniq(workoutIds);
  // load histograms for all workouts in session
  yield call(preloadAgeGroupSaga);
  const ageGroupId = yield select(StatsSelectors.getAgeGroup);
  yield put(HistogramActions.loadHistogram(uniqIds, ageGroupId));

  // load workout states
  yield call(loadWorkoutStates);

  for (const workoutId of uniqIds) {
    const isWorkoutEnabled = yield select(ConfigSelectors.isWorkoutReleasedForWeb, workoutId);
    if (!isWorkoutEnabled) {
      continue;
    }
    yield fork(loadWorkoutStats, workoutId);
  }
}

export function* openWorkoutSaga(action: PayloadAction<number>) {
  const index = action.payload;
  const workoutId = yield select(Selectors.getWorkoutIdByRelativeIndex, index);
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  yield put(AppActions.redirectExercise(exerciseId));
}

export function* saveWorkoutStateSaga(action: PayloadAction<ISaveWorkoutStatePayload>) {
  const { workoutId, goal, startDifficulty, reachDifficulty } = action.payload;
  const workoutState: ITrainingWorkoutState = {
    workoutId,
    goal,
    reachDifficulty,
    startDifficulty,
    timestamp: -1,
  };
  yield call(SessionCache.clearWorkoutStates);
  try {
    yield call(WorkoutStateService.saveTrainingWorkoutState, workoutState);
    // refresh session state after saving
    yield put(SessionActions.loadSession());
  } catch (error) {
    throw new SessionError('Workout states saving failed');
  }
}

export function* reloadSessionSaga() {
  // refresh cycles state
  yield put(CyclesActions.loadCycles());
  // reload session state
  yield put(SessionActions.loadSession());
}

export default function* sessionWatcher() {
  yield all([
    takeEvery(SessionActions.initSession, initFeatureSaga),
    takeEvery(SessionActions.openWorkout, openWorkoutSaga),
    takeEvery(SessionActions.saveWorkoutState, saveWorkoutStateSaga),
    takeEvery(SessionActions.loadSession, loadSessionDataSaga),
    takeEvery(SessionActions.reloadSession, reloadSessionSaga),
  ]);
}
